import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { Section } from '@volt/shared';

export type AppScreen = 'main' | 'settings';
export type ToastTone = 'info' | 'success' | 'error';

export interface Toast {
  id: number;
  message: string;
  tone: ToastTone;
}

export interface UIValue {
  section: Section;
  setSection: (section: Section) => void;
  activeConversationId: string | null;
  selectConversation: (id: string | null) => void;
  newChat: () => void;
  drawerOpen: boolean;
  openDrawer: () => void;
  closeDrawer: () => void;
  screen: AppScreen;
  openSettings: () => void;
  closeSettings: () => void;
  adminOpen: boolean;
  openAdmin: () => void;
  closeAdmin: () => void;
  conversationsVersion: number;
  refreshConversations: () => void;
  foldersVersion: number;
  refreshFolders: () => void;
  toasts: Toast[];
  toast: (message: string, tone?: ToastTone) => void;
  dismissToast: (id: number) => void;
}

const UIContext = createContext<UIValue | null>(null);

const EMPTY_ACTIVE: Record<Section, string | null> = {
  chat: null,
  image: null,
  video: null,
  video2: null,
  character: null,
  audio: null,
  voice: null,
  design: null,
};

let toastSeq = 0;

export function UIProvider({ children }: { children: ReactNode }) {
  const [section, setSectionState] = useState<Section>('chat');
  const [activeBySection, setActiveBySection] =
    useState<Record<Section, string | null>>(EMPTY_ACTIVE);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [screen, setScreen] = useState<AppScreen>('main');
  const [adminOpen, setAdminOpen] = useState(false);
  const [conversationsVersion, setConversationsVersion] = useState(0);
  const [foldersVersion, setFoldersVersion] = useState(0);
  const [toasts, setToasts] = useState<Toast[]>([]);

  const setSection = useCallback((next: Section) => {
    setSectionState(next);
    setScreen('main');
  }, []);

  const selectConversation = useCallback(
    (id: string | null) => {
      setActiveBySection((prev) => ({ ...prev, [section]: id }));
      setScreen('main');
    },
    [section],
  );

  const newChat = useCallback(() => {
    setActiveBySection((prev) => ({ ...prev, [section]: null }));
    setScreen('main');
  }, [section]);

  const dismissToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const toast = useCallback(
    (message: string, tone: ToastTone = 'info') => {
      const id = (toastSeq += 1);
      setToasts((prev) => [...prev, { id, message, tone }]);
      window.setTimeout(() => dismissToast(id), 4000);
    },
    [dismissToast],
  );

  const value = useMemo<UIValue>(
    () => ({
      section,
      setSection,
      activeConversationId: activeBySection[section] ?? null,
      selectConversation,
      newChat,
      drawerOpen,
      openDrawer: () => setDrawerOpen(true),
      closeDrawer: () => setDrawerOpen(false),
      screen,
      openSettings: () => setScreen('settings'),
      closeSettings: () => setScreen('main'),
      adminOpen,
      openAdmin: () => setAdminOpen(true),
      closeAdmin: () => setAdminOpen(false),
      conversationsVersion,
      refreshConversations: () => setConversationsVersion((value) => value + 1),
      foldersVersion,
      refreshFolders: () => setFoldersVersion((value) => value + 1),
      toasts,
      toast,
      dismissToast,
    }),
    [
      section,
      setSection,
      activeBySection,
      selectConversation,
      newChat,
      drawerOpen,
      screen,
      adminOpen,
      conversationsVersion,
      foldersVersion,
      toasts,
      toast,
      dismissToast,
    ],
  );

  return <UIContext.Provider value={value}>{children}</UIContext.Provider>;
}

export function useUI(): UIValue {
  const ctx = useContext(UIContext);
  if (!ctx) throw new Error('useUI must be used within UIProvider');
  return ctx;
}
